import React, { useMemo } from 'react';
import {
  HiCheck,
  HiOutlineExternalLink,
  HiDownload,
  HiPrinter,
  HiExclamation,
} from 'react-icons/hi';
import { useChecks } from '../context/ChecksContext';

export default function ChecklistSummary() {
  const {
    checklist,
    submission,
    loading,
  } = useChecks();

  const checklistItems = useMemo(() => {
    if (!checklist?.items) return [];

    return checklist.items.map((item) => {
      const result = submission?.itemResults?.find(
        (itemResult) => itemResult.checklistItemId === item.id
      );

      return {
        id: item.id,
        item: item.title,
        standard: item.description,
        result: result?.status || 'PENDING',
        comment: result?.comment || '-',
        issuePhotoUrl: result?.issuePhotoUrl || null,
        createIncident: result?.createIncident || false,
      };
    });
  }, [checklist, submission]);

  const getResultClasses = (result) => {
    switch (result) {
      case 'PASS':
        return 'bg-emerald-50 text-emerald-700 border-emerald-100';

      case 'ISSUE':
        return 'bg-rose-50 text-rose-700 border-rose-100';

      default:
        return 'bg-amber-50 text-amber-700 border-amber-100';
    }
  };

  const getResultLabel = (result) => {
    switch (result) {
      case 'PASS':
        return 'Pass';

      case 'ISSUE':
        return 'Issue';

      default:
        return 'Pending';
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-6xl rounded-lg border border-gray-100 bg-white p-6 font-sans shadow-sm">
        <div className="py-8 text-center text-sm text-gray-400">
          Loading checklist...
        </div>
      </div>
    );
  }

  if (!checklist) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-6xl rounded-lg border border-gray-100 bg-white p-6 font-sans shadow-sm">
      {/* Component Title */}
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-gray-900">
            Checklist Summary
          </h2>

          <p className="mt-1 text-xs text-gray-400">
            {checklist.name}
          </p>
        </div>

        <span className="text-xs text-gray-400">
          {checklistItems.length} items
        </span>
      </div>

      {/* No submission */}
      {!submission && (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
          <p className="text-sm font-medium text-gray-500">
            No checklist submission found for this date.
          </p>
        </div>
      )}

      {/* Main Table */}
      {submission && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  <th className="px-4 py-3 font-semibold">
                    Check Item
                  </th>

                  <th className="px-4 py-3 font-semibold">
                    Required Standard
                  </th>

                  <th className="px-4 py-3 font-semibold">
                    Result
                  </th>

                  <th className="px-4 py-3 font-semibold">
                    Comments
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 text-sm">
                {checklistItems.map((row) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:bg-gray-50/40"
                  >
                    {/* Check Item */}
                    <td className="px-4 py-3.5 font-medium text-gray-900">
                      <div className="flex items-start space-x-3">
                        {row.result === 'PASS' ? (
                          <HiCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        ) : (
                          <HiExclamation className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                        )}

                        <span>
                          {row.item}
                        </span>
                      </div>
                    </td>

                    {/* Standard */}
                    <td className="px-4 py-3.5 text-gray-500">
                      {row.standard}
                    </td>

                    {/* Result */}
                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-semibold ${getResultClasses(row.result)}`}
                      >
                        <HiOutlineExternalLink className="mr-1 h-3 w-3 rotate-90" />
                        {getResultLabel(row.result)}
                      </span>
                    </td>

                    {/* Comment */}
                    <td className="px-4 py-3.5 text-gray-600">
                      {row.comment}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bottom Actions */}
          <div className="mt-6 flex flex-col items-center justify-end space-y-2 border-t border-gray-100 pt-4 sm:flex-row sm:space-x-3 sm:space-y-0">
            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-50/40 sm:w-auto"
            >
              <HiDownload className="mr-2 h-4 w-4" />
              Download Record
            </button>

            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex w-full items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 sm:w-auto"
            >
              <HiPrinter className="mr-2 h-4 w-4" />
              Print
            </button>
          </div>
        </>
      )}
    </div>
  );
}